import { Typography, Stack, Link, styled, Theme } from '@mui/material';

import TrashDeleteIcon from 'modules/common/components/Icons/TrashDeleteIcon';
import { AsyncRowIconButton } from 'modules/common/components/RowIconButton';

/**
 * Tints the row to match a warning surface around it. Says nothing about the file itself — a
 * row can sit inside a flagged section while the file it names is perfectly fine.
 */
type UploadedFileRowColor = 'default' | 'orange';

/** Row backgrounds, both palette tokens. */
function backgroundFor(theme: Theme, rowColor: UploadedFileRowColor): string | undefined {
    return rowColor === 'orange' ? theme.palette.orange[100] : theme.palette.cement[100];
}

const RowContainer = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'rowColor' && prop !== 'isFirst' && prop !== 'isLast',
})<{ rowColor: UploadedFileRowColor; isFirst: boolean; isLast: boolean }>(({ theme, rowColor, isFirst, isLast }) => ({
    alignSelf: 'stretch',
    padding: theme.spacing(1),
    backgroundColor: backgroundFor(theme, rowColor),
    borderBottom: isLast ? 0 : '1px solid',
    borderBottomColor: isLast ? theme.palette.cement[100] : theme.palette.cement[400],
    borderTopLeftRadius: isFirst ? 8 : 0,
    borderTopRightRadius: isFirst ? 8 : 0,
    borderBottomLeftRadius: isLast ? 8 : 0,
    borderBottomRightRadius: isLast ? 8 : 0,
}));

interface UploadedFileRowProps {
    name?: string;
    sizeBytes?: number;
    sizeKB?: number;
    url?: string | null;
    onDelete?: () => void | Promise<void>;
    isFirst?: boolean;
    isLast?: boolean;
    color?: UploadedFileRowColor;
}

export default function UploadedFileRow({
    name = 'Uploaded file',
    sizeKB,
    sizeBytes,
    url,
    onDelete,
    isFirst = false,
    isLast = false,
    color = 'default',
}: UploadedFileRowProps) {
    const computedKB = typeof sizeBytes === 'number' ? sizeBytes / 1024 : sizeKB;

    return (
        <RowContainer
            direction="row"
            alignItems="center"
            spacing={1}
            rowColor={color}
            isFirst={isFirst}
            isLast={isLast}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
                {url ? (
                    <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        color="text.primary"
                        underline="hover"
                        sx={{ cursor: 'pointer' }}>
                        {name}
                    </Link>
                ) : (
                    <Typography variant="caption" color="text.primary">
                        {name}
                    </Typography>
                )}
                {computedKB ? (
                    <Typography variant="caption" color="text.secondary">
                        {`${Math.round(computedKB)}KB`}
                    </Typography>
                ) : null}
            </Stack>
            {onDelete && <AsyncRowIconButton icon={<TrashDeleteIcon />} label="Delete file" onClick={onDelete} dense />}
        </RowContainer>
    );
}
